import React from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Building2,
  Users,
  DoorOpen,
  FileText,
  Briefcase,
  LogOut,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  MessageSquare,
  Download,
  ClipboardCheck,
  BarChart2,
  Inbox,
  Sparkles,
  BookOpen,
  Megaphone,
} from 'lucide-react'
import { useAuth } from '../../lib/auth/AuthContext'
import { t } from '../../lib/i18n'
import { Avatar } from '../ui/Avatar'

interface NavItemDef {
  label: string
  to: string
  icon: React.ReactNode
}

interface SectionDef {
  title: string
  items: NavItemDef[]
}

interface AppLayoutProps {
  children: React.ReactNode
  pageTitle?: string
  actions?: React.ReactNode
  noHeader?: boolean
  fullHeight?: boolean
}

export function AppLayout({ children, pageTitle, actions, noHeader, fullHeight }: AppLayoutProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const strings = t()
  const [isExpanded, setIsExpanded] = React.useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const handleExpand = React.useCallback(() => setIsExpanded(true), [])
  const handleCollapse = React.useCallback(() => setIsExpanded(false), [])

  const sections: SectionDef[] = [
    {
      title: 'Oversigt',
      items: [
        { label: strings.nav.dashboard, to: '/dashboard', icon: <LayoutDashboard size={15} /> },
        { label: strings.nav.virksomhed, to: '/virksomhed', icon: <Briefcase size={15} /> },
      ],
    },
    {
      title: 'Ejendomme',
      items: [
        { label: strings.nav.properties, to: '/ejendomme', icon: <Building2 size={15} /> },
        { label: strings.nav.leases, to: '/lejemaal', icon: <DoorOpen size={15} /> },
        { label: strings.nav.listings, to: '/annoncering', icon: <Megaphone size={15} /> },
        { label: strings.nav.contracts, to: '/lejeforhold', icon: <FileText size={15} /> },
        { label: 'Flyttesyn', to: '/flyttesyn', icon: <ClipboardCheck size={15} /> },
      ],
    },
    {
      title: 'Lejere',
      items: [
        { label: strings.nav.renters, to: '/lejere', icon: <Users size={15} /> },
        { label: 'Henvendelser', to: '/henvendelser', icon: <Inbox size={15} /> },
        { label: 'Lejer-AI', to: '/indstillinger/lejer-ai', icon: <Sparkles size={15} /> },
      ],
    },
    {
      title: 'Økonomi',
      items: [
        { label: strings.nav.financial, to: '/okonomi', icon: <TrendingUp size={15} /> },
        { label: 'Kontoplan', to: '/kontoplan', icon: <BookOpen size={15} /> },
        { label: 'Posteringer', to: '/posteringer', icon: <FileText size={15} /> },
        { label: 'Forbrugsregnskab', to: '/forbrugsregnskab', icon: <BarChart2 size={15} /> },
        { label: strings.nav.arrears, to: '/restance', icon: <AlertTriangle size={15} /> },
        { label: strings.nav.communications, to: '/kommunikation', icon: <MessageSquare size={15} /> },
        { label: strings.nav.exports, to: '/lister', icon: <Download size={15} /> },
      ],
    },
  ]

  const activePageTitle = pageTitle ?? (() => {
    for (const section of sections) {
      for (const item of section.items) {
        if (location.pathname.startsWith(item.to)) return item.label
      }
    }
    return strings.brand.name
  })()

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#fafafb' }}>
      {/* Sidebar */}
      <aside
        onMouseEnter={handleExpand}
        onMouseLeave={handleCollapse}
        style={{
          width: isExpanded ? 220 : 52,
          flexShrink: 0,
          background: '#0c0f1a',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
          transition: 'width 0.2s ease',
          zIndex: 20,
        }}
      >
        {/* Logo */}
        <div
          className="sidebar-logo-border"
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Building2 size={14} color="white" />
          </div>
          <span
            style={{
              color: '#f1f5f9',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: '-0.01em',
              opacity: isExpanded ? 1 : 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              transition: 'opacity 0.15s ease',
            }}
          >
            {strings.brand.name}
          </span>
        </div>

        {/* Nav sections */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0', overflowX: 'hidden' }}>
          {sections.map((section, sIdx) => (
            <div key={section.title} style={{ marginBottom: 2 }}>
              {sIdx > 0 && (
                <div style={{
                  height: 1, background: '#1e2640', margin: '6px 8px',
                  opacity: isExpanded ? 1 : 0, transition: 'opacity 0.15s ease',
                }} />
              )}
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#3d4a6b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  padding: '6px 14px 3px',
                  opacity: isExpanded ? 1 : 0,
                  transition: 'opacity 0.15s ease',
                  whiteSpace: 'nowrap',
                }}
                aria-hidden={!isExpanded ? true : undefined}
              >
                {section.title}
              </div>
              {section.items.map((item) => (
                <SidebarLink key={item.to} item={item} isExpanded={isExpanded} />
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div
          className="sidebar-footer-border"
          style={{ padding: '12px 6px', flexShrink: 0 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 6px',
              borderRadius: 8,
              marginBottom: 4,
              overflow: 'hidden',
            }}
          >
            <Avatar name={user?.name ?? user?.email ?? '?'} size={28} />
            <div
              style={{
                flex: 1,
                minWidth: 0,
                opacity: isExpanded ? 1 : 0,
                transition: 'opacity 0.15s ease',
                overflow: 'hidden',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {user?.name ?? 'Bruger'}
              </div>
              <div style={{ fontSize: 11, color: '#8892a4', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {user?.email}
              </div>
            </div>
          </div>
          <LogoutButton onLogout={handleLogout} label={strings.auth.logout} isExpanded={isExpanded} />
        </div>
      </aside>

      {/* Main column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Topbar */}
        {!noHeader && (
          <header
            style={{
              height: 52,
              background: '#ffffff',
              borderBottom: '1px solid #e8eaed',
              display: 'flex',
              alignItems: 'center',
              padding: '0 28px',
              gap: 8,
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: 13 }}>
              <span style={{ color: '#94a3b8' }}>{strings.brand.name}</span>
              <ChevronRight size={14} />
              <span style={{ color: '#0f172a', fontWeight: 600 }}>{activePageTitle}</span>
            </div>
            {actions && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {actions}
              </div>
            )}
          </header>
        )}

        {/* Page content */}
        <main style={fullHeight
          ? { flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }
          : { flex: 1, overflowY: 'auto', padding: 28 }
        }>
          {children}
        </main>
      </div>
    </div>
  )
}

interface SidebarLinkProps {
  item: NavItemDef
  isExpanded: boolean
}

const SidebarLink = React.memo(function SidebarLink({ item, isExpanded }: SidebarLinkProps) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <NavLink
      to={item.to}
      aria-label={!isExpanded ? item.label : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        margin: '1px 6px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? '#f1f5f9' : hovered ? '#d1d5db' : '#8892a4',
        background: isActive ? '#1e2a4a' : hovered ? '#161b2e' : 'transparent',
        textDecoration: 'none',
        transition: 'background 0.12s, color 0.12s',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        borderLeft: isActive ? '2px solid #4f46e5' : '2px solid transparent',
        boxSizing: 'border-box',
      })}
    >
      <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>
      <span
        style={{
          opacity: isExpanded ? 1 : 0,
          transition: 'opacity 0.15s ease',
          overflow: 'hidden',
        }}
      >
        {item.label}
      </span>
    </NavLink>
  )
})

interface LogoutButtonProps {
  onLogout: () => void
  label: string
  isExpanded: boolean
}

const LogoutButton = React.memo(function LogoutButton({ onLogout, label, isExpanded }: LogoutButtonProps) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <button
      onClick={onLogout}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={!isExpanded ? label : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 6px',
        borderRadius: 7,
        fontSize: 12,
        color: hovered ? '#f1f5f9' : '#8892a4',
        background: hovered ? '#161b2e' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'background 0.12s, color 0.12s',
        fontFamily: 'inherit',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ flexShrink: 0, display: 'flex' }}><LogOut size={14} /></span>
      <span style={{ opacity: isExpanded ? 1 : 0, transition: 'opacity 0.15s ease' }}>{label}</span>
    </button>
  )
})
